import type * as React from 'react'
import { useState, useCallback, useRef, useEffect } from 'react'
import type { JSX } from 'react'

import { Flex, Input, Sidebar } from '@buffer-mono/popcorn'

import styles from './ListHeaderV2.module.css'

interface ListHeaderTitleEditorProps {
  defaultValue: string
  /**
   * Icon shown in place of the title icon while editing, typically a
   * `ListHeaderIcon` for the same feed or collection.
   */
  icon: React.ReactNode
  onUpdate: (name: string) => void | Promise<void>
  onClose: () => void
}

/**
 * Inline replacement for `ListHeaderTitleV2` while a feed or collection is
 * being renamed. Enter and blur submit the trimmed name, Escape cancels.
 * An unchanged or empty name closes the editor without calling `onUpdate`.
 */
export function ListHeaderTitleEditor({
  defaultValue,
  icon,
  onUpdate,
  onClose,
}: ListHeaderTitleEditorProps): JSX.Element {
  const [name, setName] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)
  // Enter and blur can both fire for one edit (blur while the update is in
  // flight). Only the first one may act. The guard lifts once the update
  // settles so an editor kept open after a failed update stays usable.
  const settledRef = useRef(false)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const handleClose = useCallback(() => {
    if (settledRef.current) return
    settledRef.current = true
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(() => {
    if (settledRef.current) return
    const trimmedName = name.trim()
    if (!trimmedName || trimmedName === defaultValue) {
      handleClose()
      return
    }
    settledRef.current = true
    void Promise.resolve(onUpdate(trimmedName)).finally(() => {
      settledRef.current = false
    })
  }, [name, defaultValue, onUpdate, handleClose])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSubmit()
      } else if (event.key === 'Escape') {
        handleClose()
      }
    },
    [handleSubmit, handleClose],
  )

  return (
    <Flex align="center" gap="sm">
      <Sidebar.Trigger className={styles.sidebarTrigger} />
      {icon}
      <Input
        ref={inputRef}
        aria-label="Name"
        value={name}
        onChange={(e): void => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSubmit}
      />
    </Flex>
  )
}
